import Layout from "../../components/layout/Layout";
import "./MyPlans.scss";
import React, { useRef, useState, useEffect } from "react";
import * as PR from "../../prime-modules/index";
import { useMutation, useQuery } from "react-query";
import { editTag, fetchEsimIccids, fetchEsimDetails } from "../../services/api";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { checkDataSize, checkProviderCode, displayBase64, formattedDate, shareEsimDetailsHandler, showActivateEsimUrl } from "../../utils/reuse";
import EdiText from "react-editext";
import EsimActivationDialog from "../../components/myplan/EsimActivationDialog";
import { useSelector } from "react-redux";
import { useToast } from "../../context/ToastContext";
import { track } from "@plausible-analytics/tracker";

const processEsimsData = (i) => {

  let queued = 0, active = 0, expired = 0;

  const obj = {
    ...i,
    activeBundlesData: i?.activeBundlesData?.map(e => {
      if (e.state === 'active') active += 1;
      else if (e.state === 'queued' || e.state === 'processing') queued += 1

      else expired += 1
      return e
    })
  }
  return { ...obj, queued, active, expired }

};

const sortByDate = (a, b) => moment(b.createdTs).diff(moment(a.createdTs))

const MyPlans = () => {
  const {labels, myAccount:account, messageCatalog} = useSelector(state=>state.language?.activelang);
  const {userData} = useSelector(state=> state.auth);
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState(null);
  const [showQRCode, setShowQRCode] = useState({
    status: false,
    QRCode: "",
  });
  const [activeeSIM, setActiveeSIM] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const toast = useRef();
  const {showToast} = useToast()
  const [selectedEsim, setSelectedEsim] = useState({})

  useEffect(() => {
    track("event-myesims")
  }, [])
  
  const { isLoading: bundleListLoading } = useQuery({
    queryFn: fetchEsimIccids,
    queryKey: ["esimIccids"],
    enabled: shouldFetch,
    onSuccess: async (esimList) => {
      if (esimList?.length > 0) {
        esimList = esimList?.sort(sortByDate);
        try {
          const esimDetailsPromises = esimList.map((iccidItem) =>
            fetchEsimDetails(iccidItem.iccid)
          );
          const results = await Promise.allSettled(esimDetailsPromises);
          const allEsims = [];
          results.forEach((result, index) => {
            if (result.status === "fulfilled") {
              const esimDetail = result.value;
              allEsims.push(processEsimsData(esimDetail));
            } else {
              console.error(
                `Failed to fetch eSIM details for ICCID: ${esimList[index]?.iccid}`,
                result.reason
              );
            }
          });
          setData(allEsims.sort(sortByDate));
        } catch (err) {
          console.error("Error fetching eSIM details list:", err);
        }
        setShouldFetch(false);
      }
    }
  });
  const getOrderInfo = (order) => {
    navigate("/order-details", { state: order });
  };

  const validateTagValue = (text) => {
    if (text.length === 0) {
      return true;
    } else if (text.length > 0) {
      return text.length >= 3 && text.length <= 10;
    }
  };

  const { mutate } = useMutation(editTag, {
    onSuccess: (res, variables) => {
      const {index, value} = variables?.additionalFEParams;
      setData(prev => {
        const updated = [...prev];
        if (updated[index]) {
          updated[index] = { ...updated[index], tag: value };
        }
        return updated;
      });
      showToast("success",labels?.LBLN0280);
    },
    onError: (errorMsg) => showToast("error",errorMsg.message)

  });
  
  const tagHandleSave = (rowData, value, index) => {
   
    if (validateTagValue(value) && rowData.tag !== value) {
      const params = {
        userId: rowData.userId,
        iccid: rowData.iccid,
        tag: value,
        additionalFEParams: {
          value,
          index,
        },
      };
      mutate(params);
    }
  };
  const pendingValidityHeader = (data) => <div className="expiry-column">
    <p>{data?.activeBundlesData?.[0]?.description?.split("-")[0]}</p>
  </div>
  const pendingAction = (esim) => {
    const activeBundle = esim?.activeBundlesData?.find(e => e?.state === "active") || esim?.activeBundlesData?.[0];

    return (
      <div className="flex align-items-center gap-3">
        {showActivateEsimUrl? (
          <PR.Button label={labels?.LBLN0057}
            type="submit"
            severity="secondary"
            outlined
            onClick={() => {
              setSelectedEsim(esim);
              setActiveeSIM(true);
            }}
            className="add-bundle-button"
          />
        ) : (
          <>
           <PR.Button
            label={labels?.LBLN0288}
            type="submit"
            onClick={() =>
              setShowQRCode({
                status: true,
                QRCode: esim.qrCodeImage,
              })
            }
            severity="secondary"
            outlined
            className="add-bundle-button"
          />
              {process.env.REACT_APP_ENVIRONMENT !== 'prod' &&
                <PR.Button label={labels?.LBLN0293} onClick={navigate.bind(null, "/top-up", { state: { iccid: esim.iccid, providerCode: esim.providerCode } })} type='submit' severity="secondary" outlined className="add-bundle-button" />
              }
          </>
        )}
        {activeBundle?.orderId && <i className="pi pi-share-alt" onClick={() => shareEsimDetailsHandler({activeBundle, account, toast})}></i>}
        <a href={displayBase64(esim.qrCodeImage)} download >
          <i className="pi pi-download "></i>
        </a>
        {checkProviderCode(esim.providerCode) && <i onClick={() => userData?.isEmailVerified && getOrderInfo(esim?.activeBundlesData[0])} title={!userData?.isEmailVerified ? messageCatalog["USRLGN-03"] : undefined } className={`pi pi-info-circle ${userData?.isEmailVerified ? '' : 'disabled'}`}></i>}
      </div>
    );
  };

  const bundleValidity = (rowData) => {
    let expiryDate = moment(rowData?.bundleEndTime).format("DD/MM/YYYY");
    let lastDate = new Date(expiryDate.split("/").reverse().join("-"));
    // Calculate the difference in days
    let daysRemaining = Math.ceil((lastDate - new Date()) / (1000 * 60 * 60 * 24));

    return (rowData?.state !== "active"
      ? <div className="expiry-column">
        <p>{!!rowData.state ? rowData.state : 'expired'}</p>
      </div>
      : <div className="expiry-column">
        <p>{expiryDate}</p>
        <p>{daysRemaining} {labels?.LBLN0018} {account?.ACTN0034}</p>
      </div>
    )
  }

  const activeBundleList = (rowData) => {
    return (
      <PR.DataTable value={rowData?.activeBundlesData} stripedRows className="plans-table inner-table">
        <PR.Column body={bundleName}></PR.Column>
        <PR.Column body={bundleDetails} colSpan={2} ></PR.Column>
        <PR.Column body={bundleValidity} ></PR.Column>
        <PR.Column body={(data) => bundleDetailsAction({ ...data, providerCode:rowData?.providerCode})} className="last-column"></PR.Column>
      </PR.DataTable>
    );
  }

  const yourEsim = (rowData, i) => <div className="esim-column">
    <div className="card">
      <EdiText
        name="tag"
        value={rowData.tag}
        type="text"
        placeholder={account?.ACTN0088}
        onSave={(e) => tagHandleSave(rowData, e, i.rowIndex)}
        validation={validateTagValue}
        validationMessage={account?.ACTN0089}
        onCancel={(e) => e}
        className="tag-edit"
        viewContainerClassName='custom-view-wrapper'
        saveButtonClassName="custom-save-button"
        editButtonClassName="custom-edit-button"
        cancelButtonClassName="custom-cancel-button"
        viewProps={{
          className: 'custom-view-class'
        }}
      />
      <p>ICCID: {rowData.iccid}</p>
    </div>
  </div>

  const bundleDetailsHeader = ({ activeBundlesData, active, queued, expired }) => {
    return (
      activeBundlesData?.length > 1 ? <div className="expiry-column">
        <p>
          {active ? `${active} Active` : ''}
          {active && queued ? ' & ' : ''}
          {queued ? `${queued} Queued` : ''}
          {(expired && queued) || (expired && active) ? ' & ' : ''}
          {expired ? `${expired} Expired` : ''}
        </p>
      </div> : bundleDetails(activeBundlesData?.[0])
    );
  };

  const bundleDetails = (rowData) => {
    let percentage = rowData?.totalData ? (100 - (rowData?.remainingData / rowData?.totalData) * 100)?.toFixed(0) : 0;
    return (
      <div className="expiry-column">
        <p>{rowData?.country && `${rowData?.country?.split("(")[0]} | `} {`${rowData?.isUnlimited ? labels?.LBLN0167 : `${checkDataSize(rowData?.remainingData)}  / ${checkDataSize(rowData?.totalData)} ${account?.ACTN0098?? ""}`} `}</p>
        <div className="card mt-1">
          <PR.ProgressBar
            value={percentage}
            className="progress-bar"
          ></PR.ProgressBar>
        </div>
      </div>
    );
  };

  const validityHeader = (esim) => {
    if (esim?.activeBundlesData?.length !== 1) {
      return <></>;
    }
    const isPending = esim?.status === "Released" || esim?.status === "Downloaded";
    if (isPending) {
      return pendingValidityHeader(esim);
    }
    return bundleValidity(esim?.activeBundlesData[0]);
  };

  const actionHeader = (bundle) => {
    const isPending = bundle?.status === "Released" || bundle?.status === "Downloaded";
    if (isPending) {
      return pendingAction(bundle);
    }
    return (
      <div className="flex align-items-center gap-3">
        <PR.Button
          label={labels?.LBLN0293}
          onClick={navigate.bind(null, "/top-up", { state: {iccid:bundle.iccid, providerCode: bundle.providerCode} })}
          type='submit'
          severity="secondary"
          outlined
          className="add-bundle-button"
        />
        {(bundle?.activeBundlesData?.length === 1 && checkProviderCode(bundle.providerCode)) && (
          <i onClick={() => getOrderInfo(bundle?.activeBundlesData[0])} className="pi pi-info-circle"></i>
        )}
      </div>
    );
  };

  const bundleName = ({ name }) => <div className="esim-column">
    <div className="card">
      <p className="esim-name">{name}</p>
    </div>
  </div>
  const bundleDetailsAction = bundle => <div className="flex align-items-center justify-content-end  details-button">
    {checkProviderCode(bundle.providerCode) &&
      <i onClick={getOrderInfo.bind(null, bundle)} className={`pi pi-info-circle`}></i>      
    }
  </div>

const purchasedDate = (data) => {
  const displayDate = formattedDate(data?.createdTs);

  return displayDate ? (
    <div className="expiry-column">
      <p>{displayDate}</p>
    </div>
  ) : null;
};

  return (
    <>
      <Layout>
        <EsimActivationDialog
          smdpAddress={selectedEsim.smdpAddress}
          matchingId={selectedEsim.matchingId}
          activeeSIM={activeeSIM}
          setActiveeSIM={setActiveeSIM}
          key={activeeSIM}
        />
        <section className="signup-section myplans-section">
          <div className="grid">
            <div className="col-12 text-center">
              <h1>{labels?.LBLN0172}</h1>
              <p>{account?.ACTN0099}</p>
              <div className="plans-grid-section">
                {bundleListLoading ? (
                  <div className="loading-div">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
                  </div>
                ) : (
                  <PR.DataTable
                    emptyMessage={account?.ACTN0101}
                    value={data}
                    stripedRows
                    className="plans-table"
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowExpansionTemplate={activeBundleList}
                  >
                    <PR.Column body={yourEsim} header={account?.ACTN0078}></PR.Column>
                    <PR.Column body={bundleDetailsHeader} header={account?.ACTN0102}></PR.Column>
                    <PR.Column body={validityHeader} header={account?.ACTN0105}></PR.Column>
                    <PR.Column body={purchasedDate} header={account?.ACTN0121 || "Purchased On"} style={{width:"14%"}} />
                    <PR.Column body={actionHeader} header={labels?.LBLN0163} className="last-column"></PR.Column>
                    <PR.Column expander={esim => esim?.activeBundlesData?.length > 1} />
                  </PR.DataTable>
                )}
              </div>
            </div>
          </div>
        </section>
      </Layout>
      <PR.Toast ref={toast} position="top-right" />
      <PR.Dialog
        visible={showQRCode?.status}
        onHide={() => {
          if (!showQRCode?.status) return;
          setShowQRCode((state) => ({ ...state, status: false }));
        }}
        className="QRcode-dialog"
      >
        <PR.Image
          width="350px"
          src={displayBase64(showQRCode.QRCode)}
        />
        <p className="scan-text">
          {account?.ACTN0106}
        </p>
      </PR.Dialog>
    </>
  );
};

export default MyPlans;

